import { useEffect } from 'react'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import type { FareClassWithReward } from 'constants/flight'

export interface FlashPackageCharterFallbackPricingResult {
  price: number
  memberPrice?: number
  value?: number
}

interface Props {
  offer: App.HotelOffer
  rooms: Array<App.Occupants>
  charterBundleRoute: App.CharterBundleRoute
  fareClass: FareClassWithReward
  nights: number
  packageUniqueKey: string
  feederOriginAirportCode?: string
  onResolved: (packageUniqueKey: string, result: FlashPackageCharterFallbackPricingResult | undefined) => void
}

/**
 * Renders nothing -- this exists purely to register one package's fallback price with the
 * assistant, not to display anything. Headless, one-per-package mirror of
 * `FlashHotelOptionPriceDetails.tsx`'s own `useCharterBundleFallbackPricing` call: that component
 * calls the hook once per rendered package/rate row (scoped by `packageUniqueKey`), and hooks
 * can't be called in a dynamic loop inside a single component body, so the assistant's source
 * registration mounts one instance of this component per package view instead, and collects
 * results via `onResolved` rather than rendering anything.
 */
export default function FlashPackageCharterFallbackPricingRegistration({
  offer,
  rooms,
  charterBundleRoute,
  fareClass,
  nights,
  packageUniqueKey,
  feederOriginAirportCode,
  onResolved,
}: Props) {
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer,
    rooms,
    charterBundleRoute,
    fareClass,
    nights,
    packageUniqueKey,
    feederOriginAirportCode,
  })

  useEffect(() => {
    onResolved(packageUniqueKey, fallbackPricing ? {
      price: fallbackPricing.price,
      memberPrice: fallbackMemberPricing?.price,
      value: fallbackPricing.value,
    } : undefined)
  }, [packageUniqueKey, fallbackPricing, fallbackMemberPricing, onResolved])

  // Separate from the effect above (rather than a shared cleanup) so this only clears the entry on
  // a genuine unmount -- collapse, duration change, or fallback mode toggling off -- not on every
  // ordinary price update. The parent always keys this component by `packageUniqueKey`, so a prop
  // change here would already force a remount rather than an in-place update. Without this, a
  // stale price for a since-changed occupancy/duration would survive and be reported as still
  // resolved once this component remounts -- see `isPackagePriceStillResolving` in
  // buildOfferPageSnapshot.ts, which treats any existing entry as resolved.
  useEffect(() => () => onResolved(packageUniqueKey, undefined), [packageUniqueKey, onResolved])

  return null
}
